import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { object, string } from 'yup';
import useForm, { UseFormOnSubmitHandler } from '@jwp/ott-hooks-react/src/useForm';
import { GET_ALL_DROPDOWNS, GetAllDropdownsData, GetAllDropdownsVariables, DropdownNode } from '@jwp/ott-common/src/graphql/query/video.queries';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import UploadBox from '../UploadBox/UploadBox';

import styles from './AddAdditionalImageModal.module.scss';

type AddAdditionalImageFormData = {
  labelId: string;
};

const AddAdditionalImageModal = ({ onSave, editIndex }: { onSave: () => void; editIndex?: number }) => {
  const { t } = useTranslation('upload');
  const { thumbnailSizeLimit } = useAppConfig();
  const { addAdditionalImage, additionalImages, setAdditionalImages } = useUploadStore();
  
  const isEditing = editIndex !== undefined && editIndex >= 0;
  const initialData = isEditing ? additionalImages[editIndex] : null;

  const [file, setFile] = React.useState<File | null>(initialData?.file || null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(initialData?.thumbnailUrl || null);
  const [fileError, setFileError] = React.useState<string | null>(null);

  const { data: labelData, loading: labelLoading } = useQuery<GetAllDropdownsData, GetAllDropdownsVariables>(GET_ALL_DROPDOWNS, {
    variables: {
      first: 100,
      offset: 0,
      filter: {
        ddType: { equalTo: 'IMAGE_TYPE' },
        ddValue: { notIn: ['static', 'video'] },
        ddDeletedAt: { isNull: true }
      }
    }
  });

  const labels = useMemo(() => labelData?.allDropdowns?.nodes || [], [labelData]);

  const schema = useMemo(() => {
    return object().shape({
      labelId: string().required(t('this_field_is_required', { defaultValue: 'This field is required' })),
    });
  }, [t]);

  const handleFileChange = (selectedFile: File | null) => {
    setFileError(null);
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        setFileError('thumbnail_picker.errors.invalid_file_type');
        return;
      }
      if (selectedFile.size > thumbnailSizeLimit * 1024 * 1024) {
        setFileError(t('thumbnail_picker.errors.file_size', { size: thumbnailSizeLimit }));
        return;
      }
      setPreviewUrl(URL.createObjectURL(selectedFile));
    } else {
      setPreviewUrl(null);
    }
    setFile(selectedFile);
  };

  const onSubmitHandler: UseFormOnSubmitHandler<AddAdditionalImageFormData> = (formData) => {
    if (!file && !previewUrl) {
      setFileError('upload_video.errors.select_image');
      return;
    }

    const selectedLabel = labels.find((l: DropdownNode) => l.ddId === formData.labelId);
    if (!selectedLabel) return;

    const imageData = {
      file: file || new File([], selectedLabel.ddLabel),
      thumbnailUrl: previewUrl || '',
      labelId: selectedLabel.ddId,
      labelName: selectedLabel.ddLabel,
      labelValue: selectedLabel.ddValue
    };

    if (isEditing) {
      const newImages = [...additionalImages];
      newImages[editIndex] = imageData;
      setAdditionalImages(newImages);
    } else {
      addAdditionalImage(imageData);
    }

    onSave();
  };

  const { handleSubmit, handleChange, handleBlur, values, errors, touched } = useForm<AddAdditionalImageFormData>({
    initialValues: { labelId: initialData?.labelId || '' },
    validationSchema: schema,
    onSubmit: onSubmitHandler,
  });

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>
        {isEditing ? t('upload_video.add_additional_image.edit_title', { defaultValue: 'Edit Additional Image' }) : t('upload_video.add_additional_image.title')}
      </h2>
      
      <div className={styles.uploadSection}>
        <span className={styles.label}>{t('upload_video.add_additional_image.upload_label')}</span>
        <UploadBox
          selectedFile={file}
          onFileSelect={handleFileChange}
          hint={t('upload_video.add_additional_image.hint', { size: thumbnailSizeLimit })}
          error={!!fileError}
          accept="image/*"
          height={250}
          type="image"
          buttonAlignment="right"
          previewUrl={previewUrl}
        />
        {fileError ? <div className={styles.fileError}>{t(fileError)}</div> : null}
      </div>

      <form id="add-image-form" onSubmit={handleSubmit} className={styles.form}>
        <Dropdown
          name="labelId"
          label={t('upload_video.add_additional_image.label_label')}
          value={values.labelId}
          onChange={handleChange as any}
          onBlur={handleBlur as any}
          error={!!touched.labelId && !!errors.labelId}
          helperText={touched.labelId ? (errors.labelId as string) : ''}
          required
          defaultLabel={labelLoading ? t('common:loading') : t('upload_video.add_additional_image.label_placeholder')}
          options={labels.map((l: DropdownNode) => ({ value: l.ddId, label: l.ddLabel }))}
          fullWidth
        />
        <div className={styles.helperText}>{t('upload_video.add_additional_image.label_helper')}</div>

        <div className={styles.submitContainer}>
          <Button
            type="submit"
            label={t('upload_video.add_additional_image.save_button')}
            variant="contained"
            color="primary"
            fullWidth
            disabled={!file || !values.labelId}
          />
        </div>
      </form>
    </div>
  );
};

export default AddAdditionalImageModal;
